import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false // Disable URL session detection for better performance
  },
  realtime: {
    params: {
      eventsPerSecond: 10 // Limit realtime events to prevent overwhelming
    }
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-client-info': 'lookal-web-app'
    }
  }
})

// Enhanced profile fetcher that handles RLS policies better
export const fetchUserProfile = async (userId: string) => {
  try {
    // First try regular query
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (!error && data) {
      return { data, error: null }
    }
    
    // If RLS policy is blocking, try with maybeSingle instead of single
    if (error && (error.code === '42501' || error.message?.includes('row-level security'))) {
      const { data: retryData, error: retryError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()
      
      return { data: retryData, error: retryError }
    }
    
    return { data, error }
  } catch (err) {
    console.error('Enhanced profile fetch error:', err)
    return { data: null, error: err }
  }
}

// Test connection
supabase.auth.getSession().then(({ data, error }) => {
  if (error) {
    console.error('Supabase connection test failed:', error)
  } else {
  }
}).catch(err => {
  console.error('Supabase connection error:', err)
})
// Database Types
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          email: string
          name: string
          business_name: string | null
          phone: string | null
          avatar_url: string | null
          is_admin: boolean
          is_active: boolean
          subscription_plan: 'free' | 'premium' | 'enterprise'
          subscription_expires_at: string | null
          total_campaigns: number
          total_qr_codes: number
          total_scans: number
          notification_preferences: {
            email: boolean
            push: boolean
            reports: boolean
          } | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          email: string
          name: string
          business_name?: string | null
          phone?: string | null
          avatar_url?: string | null
          is_admin?: boolean
          is_active?: boolean
          subscription_plan?: 'free' | 'premium' | 'enterprise'
          subscription_expires_at?: string | null
          total_campaigns?: number
          total_qr_codes?: number
          total_scans?: number
          notification_preferences?: {
            email: boolean
            push: boolean
            reports: boolean
          } | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          name?: string
          business_name?: string | null
          phone?: string | null
          avatar_url?: string | null
          is_admin?: boolean
          is_active?: boolean
          subscription_plan?: 'free' | 'premium' | 'enterprise'
          subscription_expires_at?: string | null
          total_campaigns?: number
          total_qr_codes?: number
          total_scans?: number
          notification_preferences?: {
            email: boolean
            push: boolean
            reports: boolean
          } | null
          created_at?: string
          updated_at?: string
        }
      }
      campaigns: {
        Row: {
          id: string
          user_id: string
          name: string
          title: string | null
          description: string | null
          type: 'Promotional' | 'Brand Awareness' | 'Seasonal' | 'Product Launch'
          status: 'Active' | 'Paused' | 'Completed' | 'Draft' | 'Scheduled'
          qr_type: 'Static QR' | 'Dynamic QR'
          cta: string
          avatar_color: 'green' | 'yellow' | 'blue' | 'purple' | 'red'
          industry: string[]
          target_audience: any
          budget: number
          spent_amount: number
          goal: string | null
          snapgoal: string[]
          media_type: string[]
          schedule: string | null
          start_date: string | null
          end_date: string | null
          preview_img: string | null
          ad_content: any
          performance_metrics: any
          total_scans: number
          conversion_rate: number
          peak_hours: string
          top_location: string
          created_at: string
          updated_at: string
          marketing_framework: string | null
          selected_video: {
            url: string
            title: string
            forced_save?: boolean
            id?: string
            video_title?: string
            video_url?: string
            video_description?: string
            industry_name?: string
            resolved_url?: string
          } | null
          video_url: string | null
          payment_bill_id: string | null
          payment_date: string | null
          call_to_action: string | null
          cta_text: string | null
        }
        Insert: {
          id?: string
          user_id: string
          name: string
          title?: string | null
          description?: string | null
          type?: 'Promotional' | 'Brand Awareness' | 'Seasonal' | 'Product Launch'
          status?: 'Active' | 'Paused' | 'Completed' | 'Draft' | 'Scheduled'
          qr_type?: 'Static QR' | 'Dynamic QR'
          cta: string
          avatar_color?: 'green' | 'yellow' | 'blue' | 'purple' | 'red'
          industry?: string[]
          target_audience?: any
          budget?: number
          spent_amount?: number
          goal?: string | null
          snapgoal?: string[]
          media_type?: string[]
          schedule?: string | null
          start_date?: string | null
          end_date?: string | null
          preview_img?: string | null
          ad_content?: any
          performance_metrics?: any
          total_scans?: number
          conversion_rate?: number
          peak_hours?: string
          top_location?: string
          created_at?: string
          updated_at?: string
          marketing_framework?: string | null
          selected_video?: {
            url: string
            title: string
            forced_save?: boolean
            id?: string
            video_title?: string
            video_url?: string
            video_description?: string
            industry_name?: string
            resolved_url?: string
          } | null
          video_url?: string | null
          payment_bill_id?: string | null
          payment_date?: string | null
          call_to_action?: string | null
          cta_text?: string | null
        }
        Update: {
          id?: string
          user_id?: string
          name?: string
          title?: string | null
          description?: string | null
          type?: 'Promotional' | 'Brand Awareness' | 'Seasonal' | 'Product Launch'
          status?: 'Active' | 'Paused' | 'Completed' | 'Draft' | 'Scheduled'
          qr_type?: 'Static QR' | 'Dynamic QR'
          cta?: string
          avatar_color?: 'green' | 'yellow' | 'blue' | 'purple' | 'red'
          industry?: string[]
          target_audience?: any
          budget?: number
          spent_amount?: number
          goal?: string | null
          snapgoal?: string[]
          media_type?: string[]
          schedule?: string | null
          start_date?: string | null
          end_date?: string | null
          preview_img?: string | null
          ad_content?: any
          performance_metrics?: any
          total_scans?: number
          conversion_rate?: number
          peak_hours?: string
          top_location?: string
          created_at?: string
          updated_at?: string
          marketing_framework?: string | null
          selected_video?: {
            url: string
            title: string
            forced_save?: boolean
            id?: string
            video_title?: string
            video_url?: string
            video_description?: string
            industry_name?: string
            resolved_url?: string
          } | null
          video_url?: string | null
          payment_bill_id?: string | null
          payment_date?: string | null
          call_to_action?: string | null
          cta_text?: string | null
        }
      }
      qr_codes: {
        Row: {
          id: string
          user_id: string
          campaign_id: string | null
          name: string
          qr_type: 'Static QR' | 'Dynamic QR'
          qr_code_url: string
          destination_url: string
          cta: string
          design_settings: any
          total_scans?: number  // DEPRECATED: Column removed from DB, calculated dynamically from qr_scans table
          status: 'Active' | 'Inactive' | 'Expired'
          expires_at: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          campaign_id?: string | null
          name: string
          qr_type?: 'Static QR' | 'Dynamic QR'
          qr_code_url: string
          destination_url: string
          cta: string
          design_settings?: any
          total_scans?: number
          status?: 'Active' | 'Inactive' | 'Expired'
          expires_at?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          campaign_id?: string | null
          name?: string
          qr_type?: 'Static QR' | 'Dynamic QR'
          qr_code_url?: string
          destination_url?: string
          cta?: string
          design_settings?: any
          total_scans?: number
          status?: 'Active' | 'Inactive' | 'Expired'
          expires_at?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      analytics_events: {
        Row: {
          id: string
          event_type: 'impression' | 'scan' | 'conversion' | 'click'
          qr_code_id: string | null
          campaign_id: string | null
          user_id: string | null
          session_id: string | null
          metadata: any | null
          created_at: string
        }
        Insert: {
          id?: string
          event_type: 'impression' | 'scan' | 'conversion' | 'click'
          qr_code_id?: string | null
          campaign_id?: string | null
          user_id?: string | null
          session_id?: string | null
          metadata?: any | null
          created_at?: string
        }
        Update: {
          id?: string
          event_type?: 'impression' | 'scan' | 'conversion' | 'click'
          qr_code_id?: string | null
          campaign_id?: string | null
          user_id?: string | null
          session_id?: string | null
          metadata?: any | null
          created_at?: string
        }
      }
      qr_scans: {
        Row: {
          id: string
          qr_code_id: string
          campaign_id: string | null
          user_agent: string | null
          ip_address: string | null
          location: any
          device_info: any
          referrer: string | null
          scan_source: string
          converted: boolean
          scanned_at: string
        }
        Insert: {
          id?: string
          qr_code_id: string
          campaign_id?: string | null
          user_agent?: string | null
          ip_address?: string | null
          location?: any
          device_info?: any
          referrer?: string | null
          scan_source?: string
          converted?: boolean
          scanned_at?: string
        }
        Update: {
          id?: string
          qr_code_id?: string
          campaign_id?: string | null
          user_agent?: string | null
          ip_address?: string | null
          location?: any
          device_info?: any
          referrer?: string | null
          scan_source?: string
          converted?: boolean
          scanned_at?: string
        }
      }
      nodes: {
        Row: {
          id: string
          name: string
          location: string
          status: 'active' | 'inactive' | 'maintenance'
          type: 'display' | 'interactive' | 'digital_billboard'
          device_type: 'display' | 'interactive' | 'digital_billboard' | 'android_tv' | 'digital_signage'
          screen_resolution: string
          device_id: string | null
          access_key: string | null
          capacity: number
          current_ads: number
          cost_per_slot: number
          description: string | null
          specifications: any
          coordinates: any
          last_heartbeat: string | null
          tv_app_version: string | null
          device_info: any
          content_config: any
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          location: string
          status?: 'active' | 'inactive' | 'maintenance'
          type?: 'display' | 'interactive' | 'digital_billboard'
          device_type?: 'display' | 'interactive' | 'digital_billboard' | 'android_tv' | 'digital_signage'
          screen_resolution?: string
          device_id?: string | null
          access_key?: string | null
          capacity?: number
          current_ads?: number
          cost_per_slot?: number
          description?: string | null
          specifications?: any
          coordinates?: any
          last_heartbeat?: string | null
          tv_app_version?: string | null
          device_info?: any
          content_config?: any
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          location?: string
          status?: 'active' | 'inactive' | 'maintenance'
          type?: 'display' | 'interactive' | 'digital_billboard'
          device_type?: 'display' | 'interactive' | 'digital_billboard' | 'android_tv' | 'digital_signage'
          screen_resolution?: string
          device_id?: string | null
          access_key?: string | null
          capacity?: number
          current_ads?: number
          cost_per_slot?: number
          description?: string | null
          specifications?: any
          coordinates?: any
          last_heartbeat?: string | null
          tv_app_version?: string | null
          device_info?: any
          content_config?: any
          created_at?: string
          updated_at?: string
        }
      }
      admin_logs: {
        Row: {
          id: string
          admin_id: string
          action: string
          resource_type: string
          resource_id: string | null
          details: any
          ip_address: string | null
          user_agent: string | null
          created_at: string
        }
        Insert: {
          id?: string
          admin_id: string
          action: string
          resource_type: string
          resource_id?: string | null
          details?: any
          ip_address?: string | null
          user_agent?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          admin_id?: string
          action?: string
          resource_type?: string
          resource_id?: string | null
          details?: any
          ip_address?: string | null
          user_agent?: string | null
          created_at?: string
        }
      }
      notifications: {
        Row: {
          id: string
          user_id: string
          campaign_id: string | null
          type: 'Slot Expiry' | 'QR Expiry' | 'Suggestion' | 'System Update' | 'Campaign' | 'General'
          title: string
          description: string
          icon: string
          is_read: boolean
          actions: string[]
          metadata: any
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          campaign_id?: string | null
          type: 'Slot Expiry' | 'QR Expiry' | 'Suggestion' | 'System Update' | 'Campaign' | 'General'
          title: string
          description: string
          icon?: string
          is_read?: boolean
          actions?: string[]
          metadata?: any
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          campaign_id?: string | null
          type?: 'Slot Expiry' | 'QR Expiry' | 'Suggestion' | 'System Update' | 'Campaign' | 'General'
          title?: string
          description?: string
          icon?: string
          is_read?: boolean
          actions?: string[]
          metadata?: any
          created_at?: string
          updated_at?: string
        }
      }
      smart_tips: {
        Row: {
          id: string
          title: string
          description: string
          category: 'campaign_optimization' | 'qr_best_practices' | 'analytics_insights' | 'general'
          tip_type: 'trending' | 'personalized' | 'general'
          target_audience: any
          is_active: boolean
          priority: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          description: string
          category: 'campaign_optimization' | 'qr_best_practices' | 'analytics_insights' | 'general'
          tip_type?: 'trending' | 'personalized' | 'general'
          target_audience?: any
          is_active?: boolean
          priority?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          description?: string
          category?: 'campaign_optimization' | 'qr_best_practices' | 'analytics_insights' | 'general'
          tip_type?: 'trending' | 'personalized' | 'general'
          target_audience?: any
          is_active?: boolean
          priority?: number
          created_at?: string
          updated_at?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

// Type exports for easier use
export type Profile = Database['public']['Tables']['profiles']['Row']
export type Campaign = Database['public']['Tables']['campaigns']['Row']
export type QRCode = Database['public']['Tables']['qr_codes']['Row']
export type AnalyticsEvent = Database['public']['Tables']['analytics_events']['Row']
export type QRScan = Database['public']['Tables']['qr_scans']['Row']
export type Node = Database['public']['Tables']['nodes']['Row']
export type AdminLog = Database['public']['Tables']['admin_logs']['Row']
export type Notification = Database['public']['Tables']['notifications']['Row']
export type SmartTip = Database['public']['Tables']['smart_tips']['Row']

// Function to create a user profile after signup
export const createUserProfile = async (userId: string, email: string, name: string, businessName?: string, phone?: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .insert([
      {
        id: userId,
        name,
        email,
        business_name: businessName,
        phone,
        is_admin: email.endsWith('@admin.com'),
        notification_preferences: {
          email: true,
          push: true,
          reports: true
        }
      }
    ])
    .select()

  return { data, error }
}

// Function to get user profile
export const getUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()

  return { data, error }
}

// Function to update user profile
export const updateUserProfile = async (userId: string, updates: Partial<Profile>) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single()

  return { data, error }
}

// Function to update user preferences
export const updateUserPreferences = async (userId: string, preferences: any) => {
  // For now, we'll store preferences in the profile table
  // In the future, you might want a separate preferences table
  const { data, error } = await supabase
    .from('profiles')
    .update({
      // Add a preferences column to the profiles table or handle it differently
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single()

  return { data, error }
}
